import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Users, Circle, MessageSquare, Send, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PlayersPage() {
  const { isAuthenticated, user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [msgTarget, setMsgTarget] = useState(null);
  const [msgContent, setMsgContent] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchPlayers();
  }, []);

  const fetchPlayers = async () => {
    try {
      const { data } = await axios.get(`${API}/players`);
      setPlayers(data);
    } catch (err) {
      console.error('Error fetching players:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!msgTarget || !msgContent.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { to_id: msgTarget.id, content: msgContent });
      toast.success(`Message sent to ${msgTarget.name}!`);
      setMsgTarget(null);
      setMsgContent('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const filtered = players.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.team_name && p.team_name.toLowerCase().includes(search.toLowerCase()))
  );

  const onlinePlayers = filtered.filter(p => p.online);
  const offlinePlayers = filtered.filter(p => !p.online);

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="players-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-4">Community</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white mb-4">
            Players
          </h1>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-8">
            {players.filter(p => p.online).length} online out of {players.length} players
          </p>

          {/* Search */}
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search players or clans..."
              className="bg-[#121212] border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 pl-11 focus:border-cyan-400"
              data-testid="player-search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Online Players */}
            {onlinePlayers.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-green-500 mb-4 flex items-center gap-2">
                  <Circle className="w-2.5 h-2.5 fill-green-500 text-green-500" />
                  Online ({onlinePlayers.length})
                </h3>
                <div className="space-y-2">
                  {onlinePlayers.map(renderPlayer)}
                </div>
              </div>
            )}

            {/* Offline Players */}
            {offlinePlayers.length > 0 && (
              <div>
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4 flex items-center gap-2">
                  <Circle className="w-2.5 h-2.5 text-zinc-600" />
                  Offline ({offlinePlayers.length})
                </h3>
                <div className="space-y-2">
                  {offlinePlayers.map(renderPlayer)}
                </div>
              </div>
            )}

            {filtered.length === 0 && (
              <div className="text-center py-16 bg-[#121212] border border-zinc-800">
                <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No players found</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Message Dialog */}
      <Dialog open={!!msgTarget} onOpenChange={() => setMsgTarget(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="message-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-cyan-400" />
              Message {msgTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendMessage} className="space-y-4 mt-4">
            <Textarea
              value={msgContent}
              onChange={(e) => setMsgContent(e.target.value)}
              placeholder="Type your message..."
              required
              className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[100px] focus:border-cyan-400 resize-none"
              data-testid="message-content-input"
            />
            <Button
              type="submit"
              disabled={sending}
              className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12"
              data-testid="send-message-button"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );

  function renderBadges(badges) {
    if (!badges || badges.length === 0) return null;
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {badges.map((b, idx) => (
          b.image_url ? (
            <div key={`${idx}-${b.design_id}`} className="flex flex-col items-center" title={b.name}>
              <img src={`${BASE}${b.image_url}`} alt={b.name || b.tier} className="w-10 h-10 object-contain" data-testid={`badge-icon-${idx}`} />
              <span className="text-[9px] text-cyan-400 font-bold truncate max-w-[56px] text-center">{b.name}</span>
            </div>
          ) : null
        ))}
      </div>
    );
  }

  function renderPlayer(player) {
    const hasBadges = player.badges && player.badges.length > 0;
    return (
      <div
        key={player.id}
        className="bg-[#121212] border border-white/5 p-3 flex items-center gap-3"
        data-testid={`player-${player.id}`}
      >
        {/* Avatar */}
        <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${
          player.online ? 'bg-green-500/10 border border-green-500/30' : 'bg-zinc-800 border border-zinc-700'
        }`}>
          <span className={`text-xs font-bold ${player.online ? 'text-green-500' : 'text-zinc-500'}`}>
            {player.name.charAt(0).toUpperCase()}
          </span>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white text-sm truncate">{player.name}</span>
            {player.team_tag && (
              <span className="text-cyan-400 text-[11px] flex-shrink-0 font-mono">[{player.team_tag}]</span>
            )}
            <Circle className={`w-2 h-2 flex-shrink-0 ${player.online ? 'fill-green-500 text-green-500' : 'text-zinc-600'}`} />
          </div>
          {player.team_name && (
            <span className="text-zinc-600 text-xs">{player.team_name}</span>
          )}
          {hasBadges && <div className="mt-1">{renderBadges(player.badges)}</div>}
        </div>

        {/* Message button */}
        {isAuthenticated && player.id !== user?.id && (
          <Button
            onClick={() => setMsgTarget(player)}
            variant="outline"
            size="sm"
            className="border-zinc-800 text-zinc-400 hover:text-cyan-400 hover:border-cyan-400 rounded-none h-8 px-3"
            data-testid={`message-player-${player.id}`}
          >
            <MessageSquare className="w-3.5 h-3.5 mr-1" />
            Message
          </Button>
        )}
      </div>
    );
  }
}
